import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, X, Plus, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * RecentMeetingFollowup
 * After a meeting with a saved person ends, gently asks the user
 * "Träffade du Sara nyss? Vad nytt fick du veta?" and saves the answer
 * as a new hook on the person card. Auto-grows the memory bank.
 */
export default function RecentMeetingFollowup() {
  const [meetings, setMeetings] = useState([]);
  const [hookText, setHookText] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/social/recent-meetings?hours=8`, { withCredentials: true });
      setMeetings(data.meetings || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  if (meetings.length === 0) return null;

  const m = meetings[0];

  const skip = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/social/people/${m.person.id}/followup-hook`,
        { event_id: m.event.id, event_starts_at: m.event.starts_at, hook: '' },
        { withCredentials: true }
      );
      setHookText('');
      load();
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };

  const save = async () => {
    const t = hookText.trim();
    if (!t) {
      toast('💛 Skriv något kort först — eller klicka "Inte just nu"');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/social/people/${m.person.id}/followup-hook`,
        { event_id: m.event.id, event_starts_at: m.event.starts_at, hook: t },
        { withCredentials: true }
      );
      toast.success(`🌸 Sparat på ${m.person.name}s kort`);
      setHookText('');
      load();
    } catch {
      toast.error('Kunde inte spara');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="neo-card p-3 mb-4 bg-gradient-to-br from-[#FFF5E6] via-[#FFE8F5] to-[#F5EEFF]"
      data-testid="recent-meeting-followup"
    >
      <div className="flex items-start gap-2">
        <div className="w-10 h-10 rounded-full border-2 border-black bg-[#C8B6FF] flex items-center justify-center text-base font-black shrink-0">
          {m.person.name.slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Memory bank · efter möte
              </p>
              <h3 className="font-black text-sm leading-tight">
                Träffade du <span className="text-[#D06892]">{m.person.name}</span> nyss?
              </h3>
              <p className="text-[11px] opacity-70 mt-0.5">
                "{m.event.title}" · skriv en sak du fick veta så minns du till nästa gång
              </p>
            </div>
            <button
              onClick={skip}
              disabled={busy}
              className="neo-btn p-1 bg-white shrink-0 disabled:opacity-50"
              data-testid="recent-meeting-skip"
              title="Inte just nu"
            >
              <X className="w-3 h-3" />
            </button>
          </div>

          <div className="flex gap-2 mt-2">
            <input
              type="text"
              value={hookText}
              onChange={(e) => setHookText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
              placeholder="t.ex. 'börjat med yoga på fredagar' eller 'flyttar i juni'"
              className="neo-input flex-1 text-sm"
              maxLength={120}
              autoFocus={false}
              data-testid="recent-meeting-input"
            />
            <button
              onClick={save}
              disabled={busy || !hookText.trim()}
              className="neo-btn px-3 py-1.5 bg-[#B9FBC0] font-bold text-xs flex items-center gap-1 disabled:opacity-50"
              data-testid="recent-meeting-save"
            >
              <Plus className="w-3 h-3" /> Spara
            </button>
          </div>

          <div className="flex items-center justify-between mt-1.5">
            <p className="text-[10px] opacity-60 italic flex items-center gap-1">
              <Users className="w-3 h-3" /> {m.person.hooks.length} hooks idag — bli {m.person.hooks.length + 1}?
            </p>
            <button
              onClick={skip}
              disabled={busy}
              className="text-[10px] underline opacity-60 hover:opacity-100"
              data-testid="recent-meeting-skip-text"
            >
              Inte just nu
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
